import {
    Box,
    Link,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useIntl } from "react-intl";
import { showBackendMessage } from "../../helpers/messagesHelper";
import Messages from "../../localization/Messages";
import { IPortDocument } from "../../models/portDocument";
import { IDocument } from "../../models/document";
import { getPortDocumentsDocks } from "../../api/portDocuments";
import { getUrlForDownloadLD } from "../../api/document";
import { getPortDocumentUrl } from "../../helpers/portDocumentUrl";

export interface IPortDocumentsViewer {
    destinationId: string;
    /** Optional empty state slot (e.g. instructions to pick a destination). */
    emptyState?: React.ReactNode;
}

/**
 * Locks &amp; Docks read-only viewer for the Dispatcher-managed port
 * information of a destination.
 */
export const PortDocumentsViewer = ({ destinationId, emptyState }: IPortDocumentsViewer) => {
    const intl = useIntl();
    const dispatch = useDispatch();
    const [items, setItems] = useState<IPortDocument[]>([]);
    const [loading, setLoading] = useState(false);

    const fetchItems = useCallback(async () => {
        if (!destinationId) {
            setItems([]);
            return;
        }
        try {
            setLoading(true);
            const res = await getPortDocumentsDocks(destinationId);
            setItems(res.data ?? []);
        } catch {
            dispatch(showBackendMessage(intl, "error", "fetching", Messages.portDocuments));
        } finally {
            setLoading(false);
        }
    }, [destinationId, dispatch, intl]);

    useEffect(() => {
        fetchItems();
    }, [fetchItems]);

    const handleDownload = async (doc: IDocument) => {
        try {
            const res = await getUrlForDownloadLD(
                doc.name,
                doc.path,
                true,
                "portDocument",
                destinationId,
            );
            window.open(res.data, "_blank");
        } catch {
            dispatch(showBackendMessage(intl, "error", "downloading", Messages.document));
        }
    };

    if (!destinationId && emptyState) {
        return <Box p={2}>{emptyState}</Box>;
    }

    return (
        <TableContainer component={Paper}>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>{intl.formatMessage(Messages.portDocumentTitle)}</TableCell>
                        <TableCell>{intl.formatMessage(Messages.document)}</TableCell>
                        <TableCell>{intl.formatMessage(Messages.weblink)}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {!loading && items.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={3} align="center">
                                {intl.formatMessage(Messages.noPortDocuments)}
                            </TableCell>
                        </TableRow>
                    )}
                    {items.map(pd => (
                        <TableRow key={pd.id}>
                            <TableCell>{pd.title}</TableCell>
                            <TableCell>
                                {pd.document ? (
                                    <Tooltip title={intl.formatMessage(Messages.downloadFile)}>
                                        <Link
                                            component="button"
                                            type="button"
                                            onClick={() => handleDownload(pd.document!)}
                                        >
                                            {pd.document.name}
                                        </Link>
                                    </Tooltip>
                                ) : (
                                    "-"
                                )}
                            </TableCell>
                            <TableCell>
                                {getPortDocumentUrl(pd.url) ? (
                                    <Link
                                        href={getPortDocumentUrl(pd.url)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {pd.url}
                                    </Link>
                                ) : (
                                    pd.url || "-"
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default PortDocumentsViewer;
